import '../stylesheets/website.css';

document.addEventListener('DOMContentLoaded', () => {
  // Theme Toggle (initialize first to prevent flash)
  initThemeToggle();

  // Mobile Menu Toggle
  initMobileMenu();

  // Intersection Observer for Fade-in Animations
  initScrollAnimations();

  // FAQ Accordion (for non-native details elements)
  initFaqAccordion();

  // Smooth scroll for anchor links
  initSmoothScroll();

  // Stats counter animation
  initStatsCounter();

  // Contact Form AJAX submission
  initContactForm();

  // Sticky CTA bar
  initStickyCTA();

  // Scroll reveal animations
  initScrollReveal();
});

/**
 * Initialize theme toggle functionality
 * Syncs with dashboard ed_theme cookie if available
 */
function initThemeToggle() {
  const themeToggle = document.querySelector('[data-theme-toggle]');
  const html = document.documentElement;

  // Get current theme from various sources
  function getCurrentTheme() {
    // 1. Check localStorage (website-specific preference)
    const stored = localStorage.getItem('ed_website_theme');
    if (stored) return stored;

    // 2. Check ed_theme cookie (sync with dashboard)
    const cookie = document.cookie.match(/ed_theme=([^;]+)/);
    if (cookie) return cookie[1];

    // 3. Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  }

  // Apply theme to document
  function applyTheme(theme) {
    if (theme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }

  // Save theme preference
  function saveTheme(theme) {
    localStorage.setItem('ed_website_theme', theme);
    // Also set cookie for potential dashboard sync
    document.cookie = `ed_website_theme=${theme};path=/;max-age=31536000;SameSite=Lax`;
  }

  // Toggle theme
  function toggleTheme() {
    const isDark = html.classList.contains('dark');
    const newTheme = isDark ? 'light' : 'dark';
    applyTheme(newTheme);
    saveTheme(newTheme);
  }

  // Initialize theme on load
  const initialTheme = getCurrentTheme();
  applyTheme(initialTheme);

  // Add click handler for toggle button
  if (themeToggle) {
    themeToggle.addEventListener('click', toggleTheme);
  }

  // Listen for system preference changes
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', e => {
      // Only auto-switch if user hasn't set a preference
      if (!localStorage.getItem('ed_website_theme')) {
        applyTheme(e.matches ? 'dark' : 'light');
      }
    });

  // Listen for storage changes (sync across tabs)
  window.addEventListener('storage', e => {
    if (e.key === 'ed_website_theme' && e.newValue) {
      applyTheme(e.newValue);
    }
  });
}

/**
 * Initialize mobile menu toggle
 */
function initMobileMenu() {
  const mobileMenuButton = document.querySelector('[data-mobile-menu-button]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const openIcon = document.querySelector('[data-menu-icon-open]');
  const closeIcon = document.querySelector('[data-menu-icon-close]');

  if (mobileMenuButton && mobileMenu) {
    mobileMenuButton.addEventListener('click', () => {
      const isHidden = mobileMenu.classList.contains('hidden');

      // Toggle menu visibility
      mobileMenu.classList.toggle('hidden');

      // Toggle button icons
      if (openIcon && closeIcon) {
        openIcon.classList.toggle('hidden', !isHidden);
        openIcon.classList.toggle('block', isHidden);
        closeIcon.classList.toggle('hidden', isHidden);
        closeIcon.classList.toggle('block', !isHidden);
      }

      // Update accessibility attribute
      mobileMenuButton.setAttribute(
        'aria-expanded',
        isHidden ? 'true' : 'false'
      );
    });

    // Close menu when clicking outside
    document.addEventListener('click', event => {
      if (
        !mobileMenuButton.contains(event.target) &&
        !mobileMenu.contains(event.target)
      ) {
        mobileMenu.classList.add('hidden');
        if (openIcon && closeIcon) {
          openIcon.classList.remove('hidden');
          openIcon.classList.add('block');
          closeIcon.classList.add('hidden');
          closeIcon.classList.remove('block');
        }
        mobileMenuButton.setAttribute('aria-expanded', 'false');
      }
    });

    // Close menu on escape key
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !mobileMenu.classList.contains('hidden')) {
        mobileMenu.classList.add('hidden');
        if (openIcon && closeIcon) {
          openIcon.classList.remove('hidden');
          openIcon.classList.add('block');
          closeIcon.classList.add('hidden');
          closeIcon.classList.remove('block');
        }
        mobileMenuButton.setAttribute('aria-expanded', 'false');
      }
    });
  }
}

/**
 * Initialize scroll-triggered fade-in animations using Intersection Observer
 */
function initScrollAnimations() {
  const animatedElements = document.querySelectorAll('.animate-on-scroll');

  if (animatedElements.length === 0) {
    // Auto-apply to sections if no explicit classes found
    autoApplyScrollAnimations();
    return;
  }

  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -10% 0px',
    threshold: 0.1,
  };

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        // Optional: stop observing after animation
        // observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  animatedElements.forEach(el => {
    observer.observe(el);
  });
}

/**
 * Auto-apply scroll animations to common elements
 * Only applies if user doesn't prefer reduced motion
 */
function autoApplyScrollAnimations() {
  // Skip animations if user prefers reduced motion
  if (prefersReducedMotion()) {
    return;
  }

  const selectors = ['.animate-on-scroll'];

  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -50px 0px',
    threshold: 0.1,
  };

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
      }
    });
  }, observerOptions);

  selectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach(el => {
      observer.observe(el);
    });
  });
}

/**
 * Initialize scroll reveal animations
 * Triggers reveal animation when elements enter viewport
 */
function initScrollReveal() {
  const revealElements = document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right');

  if (revealElements.length === 0) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target); // Only animate once
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px'
  });

  revealElements.forEach(el => observer.observe(el));
}

/**
 * Initialize FAQ accordion functionality
 * Works with both custom accordion and native details elements
 */
function initFaqAccordion() {
  // Custom accordion (non-details)
  const faqTriggers = document.querySelectorAll('.faq-trigger');

  faqTriggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const content = trigger.nextElementSibling;
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

      // Close other open items (optional - remove for multi-open)
      faqTriggers.forEach(otherTrigger => {
        if (otherTrigger !== trigger) {
          otherTrigger.setAttribute('aria-expanded', 'false');
          otherTrigger.nextElementSibling?.classList.remove('is-open');
        }
      });

      // Toggle current item
      trigger.setAttribute('aria-expanded', !isExpanded);
      content?.classList.toggle('is-open');
    });
  });

  // Enhance native details elements
  const detailsElements = document.querySelectorAll('details.faq-details');

  detailsElements.forEach(details => {
    const summary = details.querySelector('summary');

    summary?.addEventListener('click', e => {
      e.preventDefault();

      // Close other details (optional - remove for multi-open)
      detailsElements.forEach(otherDetails => {
        if (otherDetails !== details && otherDetails.open) {
          otherDetails.open = false;
        }
      });

      // Toggle with animation
      if (details.open) {
        // Closing
        const content = details.querySelector('.faq-answer');
        if (content) {
          content.style.maxHeight = `${content.scrollHeight}px`;
          requestAnimationFrame(() => {
            content.style.maxHeight = '0';
            content.style.overflow = 'hidden';
            content.style.transition = 'max-height 0.3s ease-out';
          });

          setTimeout(() => {
            details.open = false;
            content.style.maxHeight = '';
            content.style.overflow = '';
          }, 300);
        } else {
          details.open = false;
        }
      } else {
        // Opening
        details.open = true;
        const content = details.querySelector('.faq-answer');
        if (content) {
          content.style.maxHeight = '0';
          content.style.overflow = 'hidden';
          requestAnimationFrame(() => {
            content.style.transition = 'max-height 0.3s ease-out';
            content.style.maxHeight = `${content.scrollHeight}px`;
          });

          setTimeout(() => {
            content.style.maxHeight = '';
            content.style.overflow = '';
          }, 300);
        }
      }
    });
  });
}

/**
 * Initialize smooth scrolling for anchor links
 */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        // Update URL without scrolling
        history.pushState(null, '', href);
      }
    });
  });
}

/**
 * Initialize animated counter for stats with IntersectionObserver
 * Triggers count-up animation when section enters viewport
 */
function initStatsCounter() {
  const counters = document.querySelectorAll('.stat-counter');

  if (counters.length === 0) return;

  // If user prefers reduced motion, show final values immediately
  if (prefersReducedMotion()) {
    counters.forEach(counter => {
      showFinalValue(counter);
    });
    return;
  }

  const observerOptions = {
    root: null,
    threshold: 0.5,
  };

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      // Only animate once - check data-animated attribute
      if (entry.isIntersecting && !entry.target.dataset.animated) {
        entry.target.dataset.animated = 'true';
        animateCounter(entry.target);
      }
    });
  }, observerOptions);

  counters.forEach(counter => {
    observer.observe(counter);
  });
}

/**
 * Show the final value immediately (for reduced motion preference)
 */
function showFinalValue(element) {
  const target = parseFloat(element.dataset.target) || 0;
  const suffix = element.dataset.suffix || '';
  const prefix = element.dataset.prefix || '';
  const decimals = target % 1 !== 0 ? 1 : 0;

  const counterValue = element.querySelector('.counter-value');
  if (counterValue) {
    counterValue.textContent = prefix + formatNumber(target, decimals) + suffix;
  }
}

/**
 * Animate a counter from 0 to target value with ease-out easing
 */
function animateCounter(element) {
  const target = parseFloat(element.dataset.target) || 0;
  const suffix = element.dataset.suffix || '';
  const prefix = element.dataset.prefix || '';
  const decimals = target % 1 !== 0 ? 1 : 0;
  const duration = 2000;
  const start = performance.now();

  const counterValue = element.querySelector('.counter-value');
  if (!counterValue) return;

  function update(currentTime) {
    const elapsed = currentTime - start;
    const progress = Math.min(elapsed / duration, 1);

    // Ease out cubic: 1 - (1 - x)^3 (fast start, slow end)
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = target * eased;

    counterValue.textContent = prefix + formatNumber(current, decimals) + suffix;

    if (progress < 1) {
      requestAnimationFrame(update);
    }
  }

  requestAnimationFrame(update);
}

/**
 * Format number with locale-specific separators and decimal places
 */
function formatNumber(value, decimals) {
  if (decimals > 0) {
    return value.toFixed(decimals);
  }
  return Math.round(value).toLocaleString();
}

/**
 * Utility: Check if reduced motion is preferred
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Initialize contact form AJAX submission
 */
function initContactForm() {
  const form = document.getElementById('contact-form');
  if (!form) return;

  const submitBtn = document.getElementById('contact-submit-btn');
  const submitText = document.getElementById('submit-text');
  const submitArrow = document.getElementById('submit-arrow');
  const submitSpinner = document.getElementById('submit-spinner');
  const successDiv = document.getElementById('contact-success');
  const errorDiv = document.getElementById('contact-error');
  const errorMessage = document.getElementById('contact-error-message');

  // Clear field errors on input
  form.querySelectorAll('.contact-input').forEach(input => {
    input.addEventListener('input', () => {
      clearFieldError(input.name);
      input.classList.remove('border-red-500', 'dark:border-red-500');
    });
  });

  form.addEventListener('submit', async e => {
    e.preventDefault();

    // Reset UI state
    hideMessages();
    clearAllFieldErrors();

    // Show loading state
    setLoading(true);

    const formData = new FormData(form);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Show success message
        successDiv.classList.remove('hidden');
        form.reset();
        // Scroll to success message
        successDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Show error state
        errorDiv.classList.remove('hidden');

        if (data.errors) {
          const errorMessages = [];

          // Show inline field errors
          Object.entries(data.errors).forEach(([field, message]) => {
            if (field === 'base') {
              errorMessages.push(message);
            } else {
              showFieldError(field, message);
            }
          });

          if (errorMessages.length > 0) {
            errorMessage.textContent = errorMessages.join('. ');
          } else {
            errorMessage.textContent =
              'Please check the highlighted fields below.';
          }
        } else {
          errorMessage.textContent =
            data.message ||
            'Something went wrong. Please try again or email us directly.';
        }

        // Scroll to error message
        errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (error) {
      // Network or other error
      errorDiv.classList.remove('hidden');
      errorMessage.textContent =
        'Unable to send message. Please check your connection and try again.';
      errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } finally {
      setLoading(false);
    }
  });

  function setLoading(isLoading) {
    submitBtn.disabled = isLoading;
    submitText.textContent = isLoading ? 'Sending...' : 'Send Message';
    submitArrow.classList.toggle('hidden', isLoading);
    submitSpinner.classList.toggle('hidden', !isLoading);
  }

  function hideMessages() {
    successDiv.classList.add('hidden');
    errorDiv.classList.add('hidden');
  }

  function showFieldError(fieldName, message) {
    const errorEl = form.querySelector(`.field-error[data-field="${fieldName}"]`);
    const inputEl = form.querySelector(`[name="${fieldName}"]`);

    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }

    if (inputEl) {
      inputEl.classList.add('border-red-500', 'dark:border-red-500');
    }
  }

  function clearFieldError(fieldName) {
    const errorEl = form.querySelector(`.field-error[data-field="${fieldName}"]`);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
  }

  function clearAllFieldErrors() {
    form.querySelectorAll('.field-error').forEach(el => {
      el.textContent = '';
      el.classList.add('hidden');
    });
    form.querySelectorAll('.contact-input').forEach(input => {
      input.classList.remove('border-red-500', 'dark:border-red-500');
    });
  }
}

/**
 * Initialize sticky CTA bar that appears after scrolling past hero
 */
function initStickyCTA() {
  const stickyCTA = document.getElementById('sticky-cta');
  const stickyMobile = document.getElementById('sticky-cta-mobile');
  const threshold = 500; // px from top before showing
  let shown = false;
  let ticking = false;

  // Skip if elements don't exist (non-homepage)
  if (!stickyCTA && !stickyMobile) return;

  function updateStickyCTA() {
    const scrollY = window.scrollY;

    if (scrollY > threshold && !shown) {
      shown = true;
      stickyCTA?.classList.remove('translate-y-full');
      stickyMobile?.classList.remove('translate-y-24');
    } else if (scrollY <= threshold && shown) {
      shown = false;
      stickyCTA?.classList.add('translate-y-full');
      stickyMobile?.classList.add('translate-y-24');
    }

    ticking = false;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(updateStickyCTA);
        ticking = true;
      }
    },
    { passive: true }
  );

  // Check initial scroll position (for page refresh mid-scroll)
  updateStickyCTA();
}

// Disable animations if user prefers reduced motion
if (prefersReducedMotion()) {
  document.documentElement.style.setProperty('--animation-duration', '0s');
  document.documentElement.classList.add('reduce-motion');
}
